import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowLeft, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <nav className="glass-header border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COSSY NETWORK
            </span>
          </Link>
          <Link to="/">
            <Button className="btn-primary flex items-center gap-2" data-testid="back-home-btn">
              <Home className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#71717A] hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Terms of Service
        </h1>

        <div className="prose prose-invert max-w-none">
          <p className="text-[#A1A1AA] mb-6">
            Last Updated: January 2024
          </p>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              1. Educational Use Only
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                The information provided by Cossy Network Group LLC is for <strong className="text-white">educational and informational purposes only</strong> and should not be considered financial, investment, tax, or legal advice.
              </p>
              <p>
                Trading and investing in financial markets such as stocks, options, futures, forex, and cryptocurrencies involves <strong className="text-white">significant risk</strong>. You may lose part or all of your invested capital.
              </p>
              <p>
                <strong className="text-white">Past performance does not guarantee future results.</strong> Any trading decisions you make are solely your responsibility.
              </p>
              <p>
                Always conduct your own research and consider consulting with a licensed financial advisor before making any investment decisions.
              </p>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              2. No Refunds for Digital Products
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                Due to the digital nature of our educational content, <strong className="text-white">all sales are final</strong>. We do not offer refunds for:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Course purchases</li>
                <li>Subscription services (Live Trading Signals)</li>
                <li>Consulting session bookings (unless cancelled 24+ hours in advance)</li>
                <li>Any other digital products or services</li>
              </ul>
              <p>
                By purchasing our courses or subscribing to our services, you acknowledge and agree to this no-refund policy.
              </p>
              <p>
                If you experience technical issues accessing content you have purchased, please contact our support team and we will work to resolve the issue.
              </p>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              3. User Account Security
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                You are responsible for maintaining the confidentiality of your account credentials. You agree to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Use a strong, unique password for your account</li>
                <li>Not share your login credentials with others</li>
                <li>Notify us immediately of any unauthorized account access</li>
                <li>Log out of your account on shared devices</li>
              </ul>
              <p>
                <strong className="text-white">Account sharing is prohibited.</strong> Each purchase grants access to a single user. We reserve the right to terminate accounts that violate this policy without refund.
              </p>
              <p>
                We implement industry-standard security measures including encrypted connections (HTTPS), secure password hashing, and session management to protect your data.
              </p>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              4. Intellectual Property
            </h2>
            <div className="text-[#A1A1AA] space-y-4">
              <p>
                All course content, videos, PDFs, and educational materials are the intellectual property of Cossy Network Group LLC. You may not:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Redistribute, sell, or share course content</li>
                <li>Record or download video lessons without authorization</li>
                <li>Use our content for commercial purposes</li>
                <li>Claim authorship of our educational materials</li>
              </ul>
            </div>
          </div>

          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              5. Contact Information
            </h2>
            <div className="text-[#A1A1AA]">
              <p>
                For questions about these Terms of Service, please contact us at:
              </p>
              <p className="mt-4">
                <strong className="text-white">Cossy Network Group LLC</strong><br />
                Email: support@cossynetworkgroup.com
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Back to Home */}
      <footer className="bg-[#141417] border-t border-[#27272A] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link to="/">
            <Button className="btn-primary flex items-center gap-2 mx-auto" data-testid="footer-back-home">
              <Home className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
          <p className="text-[#71717A] text-sm mt-4">© 2024 Cossy Network Group LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
